<script setup>
// Google Ads ekranlarinin ortak kabugu.
//
// Her ekranin ayni ustbilgiyi (baslik, hesap adi, tarih araligi) ve ayni
// "baglanti yok" durumunu tekrar yazmasi gerekmiyor. Kabuk bunlari
// ustleniyor; ekran yalnizca icerigi cizip veriyi cekiyor.
//
// BAGLANTI KONTROLU BURADA: baglanmamis bir hesapta her ekranin kendi
// hata mesajini gostermesi yerine tek ve yonlendirici bir ekran cikiyor -
// kullaniciya hangi adimin eksik oldugu soyleniyor.
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import {
  ARALIKLAR,
  aralikOku,
  aralikYaz,
  getir,
  hataMetni,
} from './kopru.js';

const props = defineProps({
  baslik: { type: String, required: true },
  // Ayarlar ekrani baglanti olmadan da acilabilmeli.
  baglantiGerekli: { type: Boolean, default: true },
  // Tarih araligi secicisi gerekmeyen ekranlar (Kitleler, Ayarlar).
  aralikGoster: { type: Boolean, default: true },
});

const emit = defineEmits(['aralik']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const accountId = computed(() => route.params.accountId);
const baglanti = ref(null);
const yukleniyor = ref(true);
const hata = ref('');
const aralik = ref(aralikOku());

const hazir = computed(() => baglanti.value?.bagli === true);

// Eksik adimi kullanicinin anlayacagi bir cumleye ceviriyoruz.
const eksikMetni = computed(() => {
  switch (baglanti.value?.eksik_adim) {
    case 'gelistirici_jetonu':
      return t('GOOGLE_ADS.EKSIK.GELISTIRICI');
    case 'yetki':
      return t('GOOGLE_ADS.EKSIK.YETKI');
    case 'hesap':
      return t('GOOGLE_ADS.EKSIK.HESAP');
    default:
      return t('GOOGLE_ADS.EKSIK.BILINMEYEN');
  }
});

const durumuYukle = async () => {
  yukleniyor.value = true;
  hata.value = '';
  try {
    const veri = await getir(accountId.value, 'durum');
    baglanti.value = veri.baglanti;
  } catch (e) {
    hata.value = hataMetni(e, t('GOOGLE_ADS.HATA'));
  } finally {
    yukleniyor.value = false;
  }
};

const aralikDegisti = () => {
  aralikYaz(aralik.value);
  emit('aralik', aralik.value);
};

// Yeni baglanti HER ZAMAN gelen kutusu/baglanti ekranindan kuruluyor.
// Baglanti kurma girisini sayfalara dagitmak, kullanicinin "nereden
// ekleniyordu" diye aramasina yol aciyor.
const baglantiEkraninaGit = () => {
  router.push({
    name: 'settings_inbox_new',
    params: { accountId: accountId.value },
  });
};

watch(hazir, yeni => {
  if (yeni) emit('aralik', aralik.value);
});

onMounted(async () => {
  await durumuYukle();
  if (hazir.value || !props.baglantiGerekli) emit('aralik', aralik.value);
});

defineExpose({ durumuYukle, baglanti });
</script>

<template>
  <div class="flex flex-col h-full gap-4 p-6 overflow-y-auto">
    <!-- Ustbilgi -->
    <div class="flex flex-wrap items-center gap-3">
      <div class="flex flex-col min-w-0">
        <h1 class="text-xl font-medium truncate text-n-slate-12">
          {{ baslik }}
        </h1>
        <p v-if="baglanti?.hesap_adi" class="text-xs truncate text-n-slate-11">
          {{ baglanti.hesap_adi }} · {{ baglanti.musteri_id_bicimli }}
        </p>
      </div>

      <span class="grow" />

      <select
        v-if="aralikGoster && (hazir || !baglantiGerekli)"
        v-model="aralik"
        class="!mb-0 !w-auto text-sm"
        @change="aralikDegisti"
      >
        <option v-for="a in ARALIKLAR" :key="a.deger" :value="a.deger">
          {{ t(a.anahtar) }}
        </option>
      </select>

      <slot name="eylemler" />
    </div>

    <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

    <div v-if="yukleniyor" class="flex justify-center p-10">
      <Spinner class="text-n-brand" />
    </div>

    <!-- Baglanti eksik. Panelin diger bos ekranlariyla (or. Aramalar)
         ayni duzen: kullanici tanidik bir sey gorsun. -->
    <!-- Ortalama icin ayri bir sarmalayici: kabugun govdesi dar bir
         flex sutunu ve bos ekran icinde sola yasliyordu. -->
    <div
      v-else-if="baglantiGerekli && !hazir"
      class="flex items-center justify-center flex-1 w-full"
    >
      <EmptyStateLayout
        :title="t('GOOGLE_ADS.BAGLI_DEGIL')"
        :subtitle="eksikMetni"
        :show-backdrop="false"
        :action-perms="['administrator']"
      >
        <template #actions>
          <Button
            :label="t('GOOGLE_ADS.BAGLANTI_EKLE')"
            icon="i-lucide-plus"
            @click="baglantiEkraninaGit"
          />
        </template>
      </EmptyStateLayout>
    </div>

    <slot v-else :aralik="aralik" :baglanti="baglanti" />
  </div>
</template>
